import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Sparkles, TrendingUp, Users, Trophy, Flame, ExternalLink, Calendar, DollarSign,
} from "lucide-react";

export default function Partner() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/partner/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-white/60">Loading...</div>;

  return (
    <div className="space-y-7" data-testid="partner-page">
      {/* Brand header */}
      <div className="glass-card p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-64 h-64 rounded-full bg-neonyellow/10 blur-3xl pointer-events-none"/>
        <div className="flex flex-col md:flex-row md:items-center gap-5 relative">
          <div className="w-20 h-20 rounded-2xl bg-white flex items-center justify-center p-3 flex-shrink-0">
            {data.logo ? (
              <img src={data.logo} alt={data.company} className="max-w-full max-h-full object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.dataset.fallback = "true"; }}/>
            ) : null}
            {!data.logo && (
              <div className="font-display text-2xl font-black text-ink">{data.company?.[0] || "?"}</div>
            )}
          </div>
          <div className="flex-1">
            <div className="text-xs font-bold tracking-widest text-neonyellow uppercase">Partner Portal</div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-1" data-testid="partner-company">
              {data.company || "Your brand"}
            </h1>
            <p className="text-white/65 mt-1.5 text-sm">
              Welcome back, {user.name}. Here's how your sponsored content is performing.
            </p>
          </div>
          {data.website && (
            <a href={data.website} target="_blank" rel="noopener noreferrer"
              data-testid="partner-website-link"
              className="btn-secondary-neon text-sm flex items-center gap-2">
              Visit site <ExternalLink size={14}/>
            </a>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Flame} label="Sponsored challenges" value={data.sponsored_challenges.length} color="#FF2A6D" testid="partner-kpi-challenges"/>
        <Stat icon={Users} label="Total joins" value={data.joins} color="#05D9E8" testid="partner-kpi-joins"/>
        <Stat icon={Trophy} label="Completion rate" value={`${data.completion_rate}%`} color="#01FFC3" testid="partner-kpi-rate"/>
        <Stat icon={DollarSign} label="Spend (AUD)" value={`$${Math.round(data.total_paid)}`} color="#FFD700" testid="partner-kpi-spend"/>
      </div>

      {/* Sponsored challenges */}
      <section>
        <h2 className="font-display text-xl md:text-2xl font-extrabold tracking-tight">Your sponsored challenges</h2>
        <p className="text-white/55 text-sm mt-1">Live in front of users right now.</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {data.sponsored_challenges.map(c => (
            <div key={c.challenge_id} className="glass-card p-6 border-neonyellow/30" data-testid={`partner-challenge-${c.challenge_id}`}>
              <div className="flex items-center gap-2 mb-3 text-xs">
                <span className="font-bold text-neonyellow uppercase tracking-widest">★ Live</span>
                <div className="flex-1 h-px bg-neonyellow/20"/>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-neonyellow/15 text-neonyellow flex items-center justify-center"><Flame size={18}/></div>
                <div className="flex-1">
                  <div className="font-display font-bold text-lg">{c.title}</div>
                  <div className="text-xs text-white/55 flex items-center gap-1.5"><Calendar size={11}/> {c.duration_days} days</div>
                </div>
                <span className="text-xs px-3 py-1 rounded-full bg-neonyellow/15 text-neonyellow font-bold flex items-center gap-1">
                  <Trophy size={12}/> +{c.reward_points}
                </span>
              </div>
              <p className="text-sm text-white/70 mt-3">{c.description}</p>
            </div>
          ))}
          {data.sponsored_challenges.length === 0 && (
            <div className="col-span-full glass-card p-8 text-center">
              <Sparkles className="mx-auto text-neonyellow mb-3" size={28}/>
              <p className="text-white/70">No sponsored challenges yet.</p>
              <p className="text-white/50 text-sm mt-1">Once your sponsorship is set up by the team, your challenge will appear here.</p>
            </div>
          )}
        </div>
      </section>

      {/* Payments */}
      <section>
        <h2 className="font-display text-xl md:text-2xl font-extrabold tracking-tight">Payments</h2>
        <div className="glass-card mt-4 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-white/50">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Package</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map(p => (
                <tr key={p.session_id} className="border-t border-white/5" data-testid={`partner-payment-${p.session_id}`}>
                  <td className="p-3 text-white/60 text-xs">{new Date(p.created_at).toLocaleDateString("en-AU")}</td>
                  <td className="p-3 capitalize">{p.package_id}</td>
                  <td className="p-3 text-right text-neonyellow font-bold">${p.amount}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.payment_status === "paid"
                      ? "bg-neongreen/15 text-neongreen"
                      : "bg-white/10 text-white/60"}`}>{p.payment_status}</span>
                  </td>
                </tr>
              ))}
              {data.payments.length === 0 && (
                <tr><td colSpan={4} className="text-center p-8 text-white/50">No payments yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="glass-card p-6">
        <div className="flex items-start gap-3">
          <TrendingUp className="text-neoncyan flex-shrink-0 mt-0.5"/>
          <div>
            <h3 className="font-display text-lg font-bold">Want to expand your reach?</h3>
            <p className="text-sm text-white/65 mt-1">Sponsor more challenges or unlock premium placements.</p>
            <a href="/sponsor" className="btn-primary-neon text-sm mt-3 inline-flex" data-testid="partner-upgrade">View packages</a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="glass-card p-5" data-testid={testid}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}22`, color }}>
        <Icon size={18}/>
      </div>
      <div className="mt-3 font-display text-3xl font-black">{value}</div>
      <div className="text-xs text-white/55 uppercase tracking-widest">{label}</div>
    </div>
  );
}
